import { FunctionComponent, useEffect, useRef, useState } from "react"
import {
  CheckCircleTwoTone,
  DownOutlined,
  ExclamationCircleTwoTone,
  InfoCircleTwoTone,
  RightOutlined,
} from "@ant-design/icons"
import { Tooltip } from "antd"
import { SingleWaiverSection } from "./SingleWaiverSection"
import { IWaiver } from "../ClubSeasonManagement/sharedState"
import { StyledSingleWaiverSection } from "./styles"
import { IRegistrationWaiverSection } from "../ClubSeasonManagement/RegistrationConfiguration/RegistrationWaiverSubsection"
import { GuardianWaiverResponse } from "../ClubSeasonManagement/RegistrationConfiguration/RegistrationHelper"
import { GuardianWaiverMonitor } from "../Helpers/GuardianWaiverMonitor"
import { ParagraphedText } from "../ui"
import { sanitizeRichTextHtml } from "../utils/sanitizeRichTextHtml"

type TSingleWaiverProps = {
  waiver: IWaiver
  waiverResponses?: Record<string, GuardianWaiverResponse>
  waiverMonitor?: GuardianWaiverMonitor
  setExpandedWaiverId: (value?: number) => void
  expandedWaiverId?: number
}

export const SingleWaiver: FunctionComponent<TSingleWaiverProps> = ({ waiver, waiverResponses, waiverMonitor, expandedWaiverId, setExpandedWaiverId }) => {
  const expanded = waiver.waiverId === expandedWaiverId
  const [completed, setCompleted] = useState(false)
  const waiverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (waiver.waiverId === expandedWaiverId && waiverRef.current) {
      waiverRef.current.scrollIntoView({
        block: "start",
        behavior: "smooth",
      })
    }
  }, [waiver.waiverId, expandedWaiverId]);

  useEffect(() => {
    if (waiverResponses) {
      const requiredSections =
        waiver.sections?.filter((waiverSection) =>
          ["mandatory", "text input"].includes(
            waiverSection.acknowledgmentRequirement,
          ),
        ) || []

      const uncompletedRequiredSections = requiredSections.filter(
        (waiverSection) => {
          const waiverResponse =
            waiverResponses[`${waiverSection.waiverSectionId}`]

          let sectionCompleted = false

          if (waiverSection.acknowledgmentRequirement === "mandatory") {
            sectionCompleted = Boolean(waiverResponse?.guardianAcknowledged)
          } else if (
            waiverSection.acknowledgmentRequirement === "text input"
          ) {
            const response = waiverResponse?.freeformResponse || ""
            sectionCompleted = response.trim().length > 0
          }

          return !sectionCompleted
        },
      )

      setCompleted(uncompletedRequiredSections.length === 0)
    }
  }, [waiverResponses]);

  const toggleSingleWaiver = () => {
    setExpandedWaiverId(expandedWaiverId === waiver.waiverId ? undefined : waiver.waiverId)
  }
  const sections = waiver.sections || []

  const acknowledgmentSections = sections.filter(
    (waiverSection) => waiverSection.acknowledgmentRequirement === "mandatory",
  )
  const textInputSections = sections.filter(
    (waiverSection) => waiverSection.acknowledgmentRequirement === "text input",
  )

  const totalAcknowledgments = acknowledgmentSections.length
  const totalTextInputs = textInputSections.length
  const totalRequired = totalAcknowledgments + totalTextInputs

  let completedAcknowledgments = 0
  let completedTextInputs = 0

  if (waiverResponses) {
    acknowledgmentSections.forEach((waiverSection) => {
      const waiverResponse = waiverResponses[`${waiverSection.waiverSectionId}`]
      if (waiverResponse?.guardianAcknowledged) {
        completedAcknowledgments += 1
      }
    })

    textInputSections.forEach((waiverSection) => {
      const waiverResponse = waiverResponses[`${waiverSection.waiverSectionId}`]
      const response = waiverResponse?.freeformResponse || ""
      if (response.trim().length > 0) {
        completedTextInputs += 1
      }
    })
  }

  const completedRequired = completedAcknowledgments + completedTextInputs
  const hasRequiredSections = totalRequired > 0

  let headerStatusText = ""
  if (!hasRequiredSections) {
    headerStatusText = "No actions required"
  } else if (completedRequired >= totalRequired && totalRequired > 0) {
    headerStatusText = "100% complete"
  } else {
    headerStatusText = `${completedRequired} of ${totalRequired} required actions complete`
  }

  let tooltipDetails: string
  if (!hasRequiredSections) {
    tooltipDetails = "This waiver is for your information only. No actions are required."
  } else {
    const parts: string[] = []
    if (totalAcknowledgments > 0) {
      parts.push(
        `Acknowledgements: ${completedAcknowledgments} of ${totalAcknowledgments} complete`,
      )
    }
    if (totalTextInputs > 0) {
      parts.push(
        `Text responses: ${completedTextInputs} of ${totalTextInputs} complete`,
      )
    }
    tooltipDetails = parts.join("; ")
  }

  const expandednessTitle = expanded ? "Tap to collapse" : "Tap to expand"

  return (
    <div
      style={{
        border: "2px solid lightgrey",
        borderRadius: "5px",
        padding: "5px",
        minHeight: "45px",
        marginTop: "5px",
        width: "100%",
        boxSizing: "border-box",
        cursor: "pointer",
      }}
    >
      <div
        className="waiver-header"
        onClick={toggleSingleWaiver}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <div
          className="waiver-title-group"
          style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0, flexWrap: "wrap" }}
        >
          <Tooltip title={tooltipDetails || headerStatusText}>
            {!hasRequiredSections || completed ? (
              <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 18 }} />
            ) : (
              <ExclamationCircleTwoTone twoToneColor="#faad14" style={{ fontSize: 18 }} />
            )}
          </Tooltip>
          <span
            ref={waiverRef}
            id={`${waiver.waiverId}`}
            className="waiver-title"
            style={{ flexShrink: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {waiver.waiverName}
          </span>
          {headerStatusText && (
            <span
              className="waiver-status-text"
              style={{ fontSize: 12, color: "#595959" }}
            >
              {headerStatusText}
            </span>
          )}
        </div>
        <Tooltip title={expandednessTitle}>
          {expanded ? (
            <DownOutlined style={{ cursor: "pointer" }} />
          ) : (
            <RightOutlined style={{ cursor: "pointer" }} />
          )}
        </Tooltip>
      </div>
      {expanded ? (
        <div>
          <div className="waiver-body">
            <div
              dangerouslySetInnerHTML={{
                __html: sanitizeRichTextHtml(waiver.waiverDescription || ""),
              }}
            />
          </div>

          <StyledSingleWaiverSection>
            {
              waiver.sections?.map((section: IRegistrationWaiverSection, key: number) => {

                return (
                  <SingleWaiverSection waiverResponses={waiverResponses} waiverMonitor={waiverMonitor} section={section} key={key} />
                )

              })
            }
          </StyledSingleWaiverSection>
        </div>
      ) : null}
    </div>
  )
}